import React, { Fragment, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import styles from './styles.scss';
import { useParams } from 'react-router-dom';
import ReactInfiniteScroller from 'react-infinite-scroller';

import BackButton from '../../BackButton';
import ErrorState from '../../ErrorState';
import Loading from '../../Loading';
import ActionBar, {
  FilterOperatorInputBoolean,
  FilterOperatorInputDate,
  FilterOperatorInputText,
} from '../../ActionBar';
import { ResourceStatus } from '../../../types/resource';
import { FilterOperator } from '../../../types/filters';
import { SortDirection } from '../../../types/sort';

import MultiPointInstallationCaptureStore from '../../../rx-stores/multi-point-installation-captures';
import {
  HighlightableCaptureGroup,
  MultiPointInstallationCapturesActionTypes,
} from '../../../types/multi-point-installation-captures';
import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import { ON_PREM } from '../../../utils/environment';
import { DEFAULT_MAX_X_OFFSET_MULTIPLIER, DEFAULT_SPAN_MS } from '../../../utils/multi-point-installations';
import { isDateStringValid } from '../../../utils/time';

import SensorActionButtons from '../../Sensors/ActionButtons';

import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  Button,
  Checkbox,
  Dialogger,
  RadioButton,
  Skeleton,
  getNextSortDirection,
} from '@densityco/ui';
import ListView, { ListViewColumn } from '../../ListView';
import ListViewLearnMoreHeader from '../../ListViewLearnMoreHeader';
import { deserializeListViewState } from '../../../utils/rx-store-serialization';
import ColoredPercent from '../../ColoredPercent';
import { formatISOTimestamp } from '../../../utils/time';
import CaptureViewer from './CaptureViewer';
import DetailsButton from '../../DetailsButton';

const CAPTURE_GROUP_FILTER_DEFINITIONS = [
  {
    id: 'fully_annotated',
    label: 'Fully Annotated',
    operators: [
      {
        id: 'equals',
        label: 'is equal to',
        allowMultiple: false,
        isValueValid: s => typeof s === 'boolean',
        initialValue: null,
        component: FilterOperatorInputBoolean,
      } as FilterOperator<boolean>,
    ],
  },
  {
    id: 'min_video_starts_at',
    label: 'Start Date',
    operators: [
      {
        id: 'equals',
        label: 'is equal to',
        allowMultiple: false,
        isValueValid: s => s !== null && isDateStringValid(s),
        initialValue: null,
        component: FilterOperatorInputDate,
      } as FilterOperator<string>,
      {
        id: 'lt',
        label: 'is less than',
        allowMultiple: false,
        isValueValid: s => s !== null && isDateStringValid(s),
        initialValue: null,
        component: FilterOperatorInputDate,
      } as FilterOperator<string>,
      {
        id: 'gt',
        label: 'is greater than',
        allowMultiple: false,
        isValueValid: s => s !== null && isDateStringValid(s),
        initialValue: null,
        component: FilterOperatorInputDate,
      } as FilterOperator<string>,
      {
        id: 'lte',
        label: 'is less than or equal to',
        allowMultiple: false,
        isValueValid: s => s !== null && isDateStringValid(s),
        initialValue: null,
        component: FilterOperatorInputDate,
      } as FilterOperator<string>,
      {
        id: 'gte',
        label: 'is greater than or equal to',
        allowMultiple: false,
        isValueValid: s => s !== null && isDateStringValid(s),
        initialValue: null,
        component: FilterOperatorInputDate,
      } as FilterOperator<string>,
    ],
  },
  {
    id: 'annotated_events',
    label: 'Annotated Events',
    operators: [
      {
        id: 'equals',
        label: 'is equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'lt',
        label: 'is less than',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'gt',
        label: 'is greater than',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'lte',
        label: 'is less than or equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'gte',
        label: 'is greater than or equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
    ],
  },
  {
    id: 'total_accuracy',
    label: 'Total Accuracy',
    operators: [
      {
        id: 'equals',
        label: 'is equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'lt',
        label: 'is less than',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'gt',
        label: 'is greater than',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'lte',
        label: 'is less than or equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'gte',
        label: 'is greater than or equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'isnull',
        label: 'not yet calculated',
        allowMultiple: false,
        isValueValid: s => typeof s === 'boolean',
        initialValue: null,
        component: FilterOperatorInputBoolean,
      } as FilterOperator<boolean>,
    ],
  },
  {
    id: 'number_of_captures',
    label: '# of captures in capture group',
    operators: [
      {
        id: 'equals',
        label: 'is equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'lt',
        label: 'is less than',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'gt',
        label: 'is greater than',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'lte',
        label: 'is less than or equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
      {
        id: 'gte',
        label: 'is greater than or equal to',
        allowMultiple: false,
        isValueValid: s => !isNaN(parseInt(s, 10)),
        initialValue: '',
        component: FilterOperatorInputText,
      } as FilterOperator<string>,
    ],
  },
  {
    id: 'partial_capture_group',
    label: 'Partial Capture Group',
    operators: [
      {
        id: 'equals',
        label: 'is',
        allowMultiple: false,
        isValueValid: s => s !== null,
        initialValue: null,
        component: FilterOperatorInputBoolean,
      } as FilterOperator<boolean>,
    ],
  },
];

const MultiPointInstallationCaptures: React.FunctionComponent<{}> = () => {
  const state = useRxStore(MultiPointInstallationCaptureStore);
  const dispatch = useRxDispatch();

  let scrollParentRef = useRef<HTMLDivElement>(null);

  const { organizationIdOrAll, mpiId } = useParams();

  useEffect(() => {
    const {
      filters,
      searchText,
      sort,
      extra,
    } = deserializeListViewState(
      location.hash.split('?').length ? location.hash.split('?')[1] : '',
      ['capture_id', 't'],
    );

    dispatch({
      type:
        MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_PAGE,
      id: mpiId,
      params: {
        capture_id: parseInt(extra['capture_id'], 10) || undefined,
        t: parseFloat(extra['t']) || undefined,
      },
      filters,
      sort,
    });
  }, []);

  const [editModeSelectedCaptureGroupIds, setEditModeSelectedCaptureGroupIds] = useState<
    Array<HighlightableCaptureGroup['id']>
  >([]);

  const [isSingleSelect, setIsSingleSelect] = useState<boolean>(true);

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton width={200} height={24} />
            </AppBarTitle>
          </AppBar>

          <div className={styles.wrapper}>
            <div className={styles.list}>
              <ActionBar
                units="Group Captures"
                totalItemCount={null}
                selectedItemCount={0}
                onClearSelectedItems={() => { }}
                filterDefinitions={CAPTURE_GROUP_FILTER_DEFINITIONS}
                filters={state.filters}
                onChangeFilters={filters => { }}
              />

              <div className={styles.listInner}>
                <Loading />
              </div>
            </div>
            <div className={styles.viewer}>
              <AppBar />
              <Loading />
            </div>
          </div>
        </Fragment>
      );

    case ResourceStatus.ERROR:
      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton width={200} height={24} />
            </AppBarTitle>
          </AppBar>

          <ErrorState error={state.resource.error} />
        </Fragment>
      );

    case ResourceStatus.COMPLETE:
      const mpi = state.resource.data.mpi;

      // HACK: Unfortunately, there's an issue where sometimes MPI sensor positions start at 1, not
      // at zero. To work around this, detect if they are one based, and if they are, subtract 1
      // from each position.
      const mpiSensorsZeroBased = mpi.sensors.find(i => i.position === 0);
      if (!mpiSensorsZeroBased) {
        mpi.sensors = mpi.sensors.map(s => ({
          ...s,
          position: s.position - 1,
        }));
      }

      const selectedCaptureGroupId = state.resource.data.selectedCaptureGroupId;

      let captureGroup = state.resource.data.captureGroups.groups.find(
        i => i.id === selectedCaptureGroupId,
      );

      // TODO - because of pagination, the selectedCaptureGroupId may not be loaded yet
      if (!captureGroup) {
        captureGroup = state.resource.data.captureGroups.groups[0];
      }

      const selectedSensors = (
        mpi.sensors
          .filter(s => s.doorway)
          .map(item => ({
            ...item,
            serial_number: item.serial_number,
            link_id: item.link_id,
            doorway_name: item.doorway_name,
            organization: {
              id: item.organization.id,
              name: item.organization.name,
            },
            sensor_type: item.sensor_type,
            os_version: item.os_version,
            diagnostic_info_json: item.diagnostic_info_json,
            in_mpi: true,
          }))
      );

      const header = (
        <AppBar>
          <AppBarSection>
            <BackButton />
            <div className={styles.name}>
              {mpi.name}
              <ul className={styles.nameMetadata}>
                <li className={styles.nameMetadataItem}>
                  Total Accuracy: <strong>
                    <ColoredPercent
                      percent={
                        state.resource.data.captureGroups.total_accuracy
                      }
                    />
                  </strong>
                </li>
                <li className={styles.nameMetadataItem}>
                  Sensor Height: <strong>{mpi.sensor_height}&quot;</strong>
                </li>
                <li className={classnames(styles.nameMetadataItem, styles.extra)}>
                  Span: <strong>{mpi.config.span_ms || DEFAULT_SPAN_MS}ms</strong>
                </li>
                <li className={classnames(styles.nameMetadataItem, styles.extra)}>
                  Max X Offset Multiplier: <strong>{mpi.config.max_x_offset_multiplier || DEFAULT_MAX_X_OFFSET_MULTIPLIER}</strong>
                </li>
              </ul>
            </div>
          </AppBarSection>
          <AppBarSection>
            <SensorActionButtons
              presentation="detail"
              sensors={selectedSensors}
              primaryActionButton={
                <Button
                  variant="filled"
                  href={`#/${organizationIdOrAll}/multi-point-installations/${mpiId}/edit`}
                >
                  Edit MPI Configuration
                </Button>
              }
            />
          </AppBarSection>
        </AppBar>
      );

      if (mpi.sensors.length < 2) {
        return (
          <Fragment>
            {header}

            <div
              className={styles.centeredWrapper}
              data-test-id="mpi-captures-not-enough-sensors"
            >
              <h3>
                This MPI contains {mpi.sensors.length} sensor{mpi.sensors.length !== 1 ? 's' : ''}.
              </h3>
              <p>MPIs must contain at least two sensors to function correctly.</p>
            </div>
          </Fragment>
        );
      }

      const deleteCaptureGroups = async (captureGroupIds: Array<HighlightableCaptureGroup['id']>) => {
        const ok = await Dialogger.confirm({
          prompt: (
            <Fragment>
              Are you sure you would like to delete{' '}
              {captureGroupIds.length === 1 ? (
                <Fragment>
                  capture group <strong>{captureGroupIds[0]}</strong>
                </Fragment>
              ) : (
                <Fragment>
                  <strong>{captureGroupIds.length}</strong> capture groups
                </Fragment>
              )}?
            </Fragment>
          ),
        })

        if (!ok) {
          return;
        }

        dispatch({
          type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS,
          mpiId: mpi.id,
          ids: captureGroupIds
        })

      }

      const deleteCaptureGroupsButton = (
        <Button
          disabled={!editModeSelectedCaptureGroupIds.length}
          onClick={() => deleteCaptureGroups(editModeSelectedCaptureGroupIds)}
          data-test-id="mpi-captures-delete-button"
        > Delete
        </Button>
      )

      const onClickCaptureGroupRow = (item) => {
        if (isSingleSelect) {
          dispatch({
            type:
              MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_SELECT_CAPTURE_GROUP,
            id: item.id,
          });
        } else {
          if (editModeSelectedCaptureGroupIds.includes(item.id)) {
            const itemIndex = editModeSelectedCaptureGroupIds.indexOf(item.id);
            setEditModeSelectedCaptureGroupIds(
              editModeSelectedCaptureGroupIds.slice(0, itemIndex)
                .concat(editModeSelectedCaptureGroupIds.slice(itemIndex + 1))
            )
          } else {
            setEditModeSelectedCaptureGroupIds(editModeSelectedCaptureGroupIds.concat(item.id));

          }
        }
      }

      const allCaptureGroupsSelected = () => {
        if (state.resource.status !== ResourceStatus.COMPLETE) {
          return false;
        }
        return editModeSelectedCaptureGroupIds.length === state.resource.data.captureGroups.groups.length;
      }

      const list = (
        <div className={styles.list}>
          <ActionBar
            units="Capture Groups"
            totalItemCount={state.resource.data.captureGroups.total}
            selectedItemCount={editModeSelectedCaptureGroupIds.length}
            onClearSelectedItems={() => setEditModeSelectedCaptureGroupIds([])}
            showSelectTypeToggle={true}
            isSingleSelect={isSingleSelect}
            onChangeSelectType={() => {
              setIsSingleSelect(!isSingleSelect);
              setEditModeSelectedCaptureGroupIds([]);
              dispatch({
                type:
                  MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_SELECT_CAPTURE_GROUP,
                id: null,
              });
            }}
            editModeButtons={deleteCaptureGroupsButton}
            filterDefinitions={CAPTURE_GROUP_FILTER_DEFINITIONS}
            filters={state.filters}
            onChangeFilters={filters =>
              dispatch({
                type:
                  MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_FILTERS,
                filters,
              })
            }
          />
          <div className={styles.listInner} ref={scrollParentRef} data-test-id="mpi-captures-capture-list">
            <ReactInfiniteScroller
              threshold={1700}
              pageStart={1}
              loadMore={page => {
                dispatch({
                  type:
                    MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_SET_PAGE,
                  value: page,
                  mpiId: mpiId,
                });
              }}
              hasMore={
                state.resource.data.captureGroups.total >
                state.resource.data.captureGroups.groups.length &&
                state.resource.data.fetchingMoreMPIs.status ===
                ResourceStatus.IDLE
              }
              useWindow={false}
              getScrollParent={() => scrollParentRef.current}
            >
              <ListView
                data={state.resource.data.captureGroups.groups}
                padOuterColumns
                isRowHighlighted={(item: HighlightableCaptureGroup) =>
                  item.highlighted
                }
                headerHeight={48}
                sort={[state.sort]}
                onClickRow={item => onClickCaptureGroupRow(item)}
                onClickHeader={(column: string) => {
                  // Don't allow fitlering these columns
                  if (column === '') {
                    return;
                  }

                  const lastSortDirection =
                    column === state.sort.column
                      ? state.sort.direction
                      : 'none';
                  const sortDirection: SortDirection = getNextSortDirection(lastSortDirection);

                  dispatch({
                    type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_SORT,
                    column: column,
                    direction: sortDirection,
                  });
                }}
              >
                <ListViewColumn
                  id=""
                  title={
                    isSingleSelect
                      ? ''
                      : <Checkbox
                        checked={allCaptureGroupsSelected()}
                        onClick={() => {
                          if (allCaptureGroupsSelected()) {
                            setEditModeSelectedCaptureGroupIds([]);
                          } else {
                            if (state.resource.status !== ResourceStatus.COMPLETE) {
                              return;
                            }
                            setEditModeSelectedCaptureGroupIds(state.resource.data.captureGroups.groups.map(i => i.id))
                          }
                        }}
                      />}
                  width={60}
                  template={(item: HighlightableCaptureGroup) => {
                    if (isSingleSelect) {
                      return (<RadioButton
                        checked={item.id === selectedCaptureGroupId}
                        onChange={() => { }}
                      />)
                    } else {
                      return (<Checkbox
                        checked={editModeSelectedCaptureGroupIds.includes(item.id)}
                        onChange={() => onClickCaptureGroupRow(item)}
                        data-test-id="mpi-captures-capture-group-item"
                      />
                      )
                    }
                  }}
                />
                <ListViewColumn
                  id="min_video_starts_at"
                  title="Start Date"
                  template={(item: HighlightableCaptureGroup) => (
                    <div>
                      {formatISOTimestamp(item.min_video_starts_at, true)}
                    </div>
                  )}
                />
                {!ON_PREM ? (
                  <ListViewColumn
                    id="annotated_events"
                    width={120}
                    title={
                      <span style={{ textAlign: 'left' }}>
                        Annotated <br />
                        Events
                      </span>
                    }
                    template={(item: HighlightableCaptureGroup) =>
                      item.annotated_events
                    }
                  />
                ) : null}
                {!ON_PREM ? (
                  <ListViewColumn
                    id="number_of_non_fully_annotated_captures"
                    title={
                      <ListViewLearnMoreHeader
                        header="Fully Annotated"
                        tooltip={
                          <Fragment>
                            Indicates whether all component captures of the
                            capture group are fully annotated and scored.
                            <br />
                            <br />
                            Capture groups that say 'No' here are still
                            being processed in the algo pipeline; check back
                            later.
                          </Fragment>
                        }
                      />
                    }
                    template={(item: HighlightableCaptureGroup) => (
                      <Fragment>
                        <div
                          className={classnames(
                            styles.fullyAnnotatedCaptureGroupsStatus,
                            {
                              [styles.yes]:
                                item.fully_annotated_capture_count ===
                                item.captures.length,
                            },
                          )}
                        >
                          {item.fully_annotated_capture_count ===
                            item.captures.length
                            ? 'Yes'
                            : 'No'}
                        </div>
                        <div
                          className={
                            styles.fullyAnnotatedCaptureGroupsSummary
                          }
                        >
                          {item.fully_annotated_capture_count} /{' '}
                          {item.captures.length}
                        </div>
                      </Fragment>
                    )}
                  />
                ) : null}
                {!ON_PREM ? (
                  <ListViewColumn
                    id="total_accuracy"
                    width={160}
                    title={
                      <ListViewLearnMoreHeader
                        header="Dedupe Accuracy"
                        tooltip={
                          <Fragment>
                            The accuracy determined for the capture group
                            after taking all events from each capture,
                            running them through the MPI deduplication
                            algorithm, and scoring the resulting annotations
                            and algo events.
                            <br />
                            <br />
                            This is the canonical "accuracy score" for an
                            MPI.
                          </Fragment>
                        }
                      />
                    }
                    template={(item: HighlightableCaptureGroup) => (
                      <ColoredPercent percent={item.total_accuracy} />
                    )}
                  />
                ) : null}
                <ListViewColumn
                  id="Details"
                  key="details"
                  title=" "
                  width={60}
                  template={(item: HighlightableCaptureGroup) => (
                    <span style={{ display: 'flex', alignItems: 'center', height: 56 }}>
                      <DetailsButton
                        size="small"
                        href={`#/${organizationIdOrAll}/multi-point-installations/${mpiId}/captures/${item.id}`}
                      />
                    </span>
                  )}
                />
              </ListView>
            </ReactInfiniteScroller>
          </div>

          {state.resource.data.fetchingMoreMPIs.status ===
            ResourceStatus.LOADING ? (
            <div className={styles.infiniteScrollLoading}>
              Loading more capture groups...
            </div>
          ) : null}
          {state.resource.data.fetchingMoreMPIs.status ===
            ResourceStatus.ERROR ? (
            <div
              className={classnames(
                styles.infiniteScrollLoading,
                styles.error,
              )}
            >
              Error loading capture groups:{' '}
              {state.resource.data.fetchingMoreMPIs.error.toString()}
            </div>
          ) : null}
        </div>
      );

      // Show an empty state if no captures were found
      if (state.resource.data.captureGroups.groups.length === 0) {
        return (
          <Fragment>
            {header}
            <div className={styles.wrapper}>
              {list}
              <div
                className={styles.centeredWrapper}
                data-test-id="mpi-captures-no-captures"
              >
                <h3>No capture groups were found.</h3>
                <p>
                  When all sensors in a MPI capture data at the same time,{' '}
                  a capture group is created.
                  <br />
                  Click <strong>Trigger Data Capture</strong> above to create captures.
                </p>
              </div>
            </div>
          </Fragment>
        );
      }

      // If no capture is currently selected, then show anotehr special empty state
      if (selectedCaptureGroupId === null) {
        return (
          <Fragment>
            {header}
            <div className={styles.wrapper}>
              {list}
              <div className={styles.centeredWrapper}>
                {isSingleSelect ? 'No capture group selected' : 'Currently in edit mode'}
              </div>
            </div>
          </Fragment>
        );
      }

      return (
        <Fragment>
          {header}
          <div className={styles.wrapper}>
            {list}
            <div className={styles.viewer}>
              <CaptureViewer
                captureGroup={captureGroup}
                mpi={mpi}
                seek={state.resource.data.seek}
                selectedCaptureGroupId={selectedCaptureGroupId}
              />
            </div>
          </div>
        </Fragment>
      );

    default:
      return null;
  }
};

export default MultiPointInstallationCaptures;
